import { BlockEmbed } from 'quill/blots/block';
import { Delta } from 'quill/core';
import type { Parchment } from 'quill/core';
import type { RenderBlock } from '../../../utils/deltaToRenderBlocks';
import { renderBlocksToDelta } from '../../../utils/deltaToRenderBlocks';
import { findBlotByNode } from '../../../utils/findByNode';
import findQuillByBlot from '../../../utils/findQuillByBlot';

const observer = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;

    observer.unobserve(entry.target);
    const blot = findBlotByNode(entry.target);
    const quill =
      blot instanceof DelayedRenderBlockGroup && findQuillByBlot(blot);

    if (quill) {
      quill.updateContents(
        new Delta()
          .retain(blot.offset(blot.scroll))
          .delete(1)
          .concat(renderBlocksToDelta(blot.renderBlocks)),
      );
    }
  });
});

const getEstimatedHeight = (renderBlock: RenderBlock) =>
  renderBlock.type === 'text' ? 40 : 200;

class DelayedRenderBlockGroup extends BlockEmbed {
  static blotName = 'delayed-render-block-group';
  static className = 'ql-delayed-render-block-group';
  static tagName = 'div';

  renderBlocks: RenderBlock[];

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: Node,
    value: RenderBlock[],
  ) {
    super(scroll, domNode);

    this.renderBlocks = value;
    this.domNode.style.height = `${value.reduce(
      (height, renderBlock) => height + getEstimatedHeight(renderBlock),
      0,
    )}px`;
  }

  attach() {
    super.attach();
    observer.observe(this.domNode);
  }

  detach() {
    super.detach();
    observer.unobserve(this.domNode);
  }
}

export default DelayedRenderBlockGroup;
